// The avatar and the account panel (APPLE-SPEC §5.7): a 28 px light monogram in a 32 px hit area, 12 px after the last glyph button; name
// and role are never shown in the bar. The panel (300 px, the menu material, radius 8): the identity (40 px monogram, the name, title ·
// department, clearance) · the Ministry's name in English and Arabic · Language `English | العربية` and Appearance `Light | Dark |
// Automatic` as segmented controls · Sign-in and security · Trust Centre · Accessibility and display… · Keyboard shortcuts ⌘/ · the
// administrators' console · the session ("Secure session · password · Idle sign-out in 26:41 · last sign-in …"; "password + authenticator"
// only on a host that requires a second factor) · Sign out.
// Help and Report an issue live in the status bar's Help menu (and here on a phone, where the status bar is hidden); the department switch
// lives in the agent inspector. It is a dialog (Tab reaches the segmented controls); ↑ ↓ move between its rows. WP-SHELL.
import { lazy, Suspense, useState, type KeyboardEvent } from "react";
import { useAuth, useUserPhoto } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Popover, PopoverContent, PopoverTrigger } from "@ncp/ui";
import { AccessibilityDialog } from "./AccessibilityMenu";

// The panel's content loads the first time the avatar is opened (AccountPanel.tsx).
const loadPanel = () => import("./AccountPanel");
const AccountPanel = lazy(() => loadPanel().then((m) => ({ default: m.AccountPanel })));

/**
 * Two initials from a name, without honorifics ("Dr.", "Prof.", "H.E.", «د.»), a parenthesised note ("DT Administrator (test)" → "DT",
 * an acronym first word is kept whole) or punctuation: only letters are ever taken, so a bracket or a quote never shows ("D(", ")D").
 */
export function initialsOf(name: string): string {
  const words = name
    .replace(/\([^)]*\)?/g, " ")
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}\p{N}.]/gu, ""))
    .filter((w) => /\p{L}/u.test(w) && !/^(dr|prof|h\.?e|mr|mrs|ms|eng)\.?$/i.test(w) && !/^(د|أ\.?د|م)\.?$/.test(w));
  if (words[0] && /^[A-Z]{2,3}$/.test(words[0])) return words[0].slice(0, 2);
  const pick = words.length > 1 ? [words[0]!, words[words.length - 1]!] : words;
  return pick.map((w) => (/\p{L}/u.exec(w)?.[0] ?? "").toUpperCase()).join("").slice(0, 2) || "·";
}

/** Arrow keys move between the panel's rows (a macOS menu feel); Tab still reaches every control. */
function moveRows(e: KeyboardEvent<HTMLDivElement>) {
  if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
  const rows = [...e.currentTarget.querySelectorAll<HTMLElement>(".mrow:not([aria-disabled='true'])")];
  if (!rows.length) return;
  e.preventDefault();
  const i = rows.indexOf(document.activeElement as HTMLElement);
  const next = e.key === "ArrowDown" ? (i + 1) % rows.length : (i - 1 + rows.length) % rows.length;
  rows[i < 0 ? 0 : next]?.focus();
}

export function AccountMenu({ onOpenAbout }: { onOpenAbout: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [display, setDisplay] = useState(false);
  const photo = useUserPhoto(user?.photoUrl);
  if (!user) return null;

  const name = (ar && user.displayNameAr) || user.displayName;
  const title = (ar && user.titleAr) || user.title || "";
  const initials = initialsOf(user.displayName);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="es-me" aria-label={t("assistant:econic.shell.account.menu", { name: title ? `${name}, ${title}` : name })} title={name} aria-haspopup="dialog" data-econic-account="" onPointerEnter={() => void loadPanel()} onFocus={() => void loadPanel()}>
            <span className="es-avatar" aria-hidden>{photo ? <img src={photo} alt="" /> : initials}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" sideOffset={6} collisionPadding={8} className="es-pop menu es-acct" aria-label={t("assistant:econic.shell.account.title")} onKeyDown={moveRows}>
          <Suspense fallback={<div className="min-h-[380px]" aria-hidden />}>
            <AccountPanel onClose={() => setOpen(false)} onOpenAbout={onOpenAbout} onOpenDisplay={() => setDisplay(true)} />
          </Suspense>
        </PopoverContent>
      </Popover>
      <AccessibilityDialog open={display} onOpenChange={setDisplay} />
    </>
  );
}
